import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { DocumentTextIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../common/Button";
import TemplateSelector from "../common/TemplateSelector";
import { api } from "./api";

/**
 * Draft a police report from the investigation. Creates a case in the
 * officer's Cases list and hands the investigation's verified findings to
 * the standard report pipeline; the editor's processing state takes over
 * after navigation. Instructions are optional steering, not required.
 */
const DraftReportModal: React.FC<{ open: boolean; onClose: () => void; investigationId: string; investigationName: string }> = ({
  open,
  onClose,
  investigationId,
  investigationName,
}) => {
  const navigate = useNavigate();
  const [instructions, setInstructions] = useState("");
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const submit = async () => {
    setSubmitting(true);
    try {
      const { caseId } = await api.exportToCase(investigationId, { instructions: instructions.trim() || undefined, reportTemplateId: templateId || undefined });
      toast.success("Drafting the report — opening the case.");
      navigate(`/cases/${caseId}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Failed to draft the report");
      setSubmitting(false);
    }
  };

  return (
    <dialog open className="modal modal-open">
      <div className="modal-box max-w-lg p-0 overflow-hidden rounded-2xl">
        <div className="flex items-start gap-3 px-6 pt-6 pb-4">
          <div className="size-10 shrink-0 rounded-xl bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 flex items-center justify-center">
            <DocumentTextIcon className="size-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-base-content leading-tight">Draft a report from this investigation</h3>
            <p className="text-sm text-base-content/60 mt-0.5">
              A new case is created in your Cases list. The narrative is written from the brief, your confirmed flagged moments, the
              analyzed calls, and the case documents — in your report style, with recorded statements attributed to their calls.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-md text-base-content/50 hover:bg-base-200 hover:text-base-content" title="Close">
            <XMarkIcon className="size-5" />
          </button>
        </div>
        <div className="px-6 space-y-3">
          <div>
            <label className="text-xs font-semibold text-base-content/70">Instructions (optional)</label>
            <textarea
              className="textarea textarea-bordered bg-base-100 w-full mt-1 text-sm focus:outline-none focus:border-blue-500"
              rows={3}
              placeholder={`e.g. Focus on the witness-tampering findings. Address it as a supplement to case ${investigationName}.`}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-base-content/70">Report template (optional)</label>
            <div className="mt-1">
              <TemplateSelector selectedTemplateId={templateId} onSelect={setTemplateId} />
            </div>
          </div>
          <p className="text-[11px] text-base-content/50">
            Only moments you confirmed are presented as findings; unreviewed AI flags are noted as unverified. Verify quotes against the
            audio before filing.
          </p>
        </div>
        <div className="px-6 py-5 flex items-center justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={submit} disabled={submitting}>
            {submitting ? "Drafting…" : "Draft report"}
          </Button>
        </div>
      </div>
      <button type="button" className="modal-backdrop" onClick={onClose} aria-label="Close" />
    </dialog>
  );
};

export default DraftReportModal;
